import { HTMLAttributes } from 'react';
import { Row, UseTableInstanceProps } from 'react-table';
import cn from 'classnames';
import {
  protectCustomColumn,
  hasUserLockProperties,
  hasUserAccountClosedProperty,
} from 'utils/typeGuards';
import styles from './TableBody.module.css';

type Props<D extends object> = Pick<
  UseTableInstanceProps<D>,
  'getTableBodyProps' | 'rows' | 'prepareRow'
> & {
  onRowClick?: (row: Row<D>) => void;
};

const TableBody = <D extends object>({
  getTableBodyProps,
  rows,
  prepareRow,
  onRowClick,
}: Props<D>) => (
  <tbody {...getTableBodyProps()}>
    {rows.map((row) => {
      prepareRow(row);
      const isUserLocked =
        hasUserLockProperties(row.original) &&
        (row.original.isAutoLocked || row.original.isLocked);

      const isAccountClosed =
        hasUserAccountClosedProperty(row.original) && row.original.accountClosed;

      const profileStyle: HTMLAttributes<unknown>['className'] = isAccountClosed
        ? isUserLocked
          ? styles.closed_and_locked
          : styles.closed
        : isUserLocked
        ? styles.locked
        : undefined;

      const { key: rowKey, ...rowProps } = row.getRowProps();

      return (
        <tr
          key={rowKey}
          {...rowProps}
          className={cn(styles.row, profileStyle)}
          onClick={onRowClick?.bind(null, row)}
        >
          {row.cells.map((cell) => {
            const { key: cellKey, ...cellProps } = cell.getCellProps();

            return (
              <td
                key={cellKey}
                {...cellProps}
                className={styles.cell}
                onClick={cell.value?.props?.onClick?.bind(null, row)}
                title={
                  protectCustomColumn(cell.column)?.cellTitle?.(cell.value) ?? cell.value
                }
              >
                {cell.render('Cell')}
              </td>
            );
          })}
        </tr>
      );
    })}
  </tbody>
);

export default TableBody;
